/**
 * usePermissions — TanStack Query hooks for /api/eraf/permissions CRUD.
 *
 * queryKey pattern: ['permission-management', 'list', filters] / ['permission-management', 'detail', id]
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { permissionApi } from '../api/permissionApi';
import type { PermissionListFilter, CreatePermissionRequest, UpdatePermissionRequest } from '../api/permissionApi';

// ─────────────────────────────────────────────────────────────────────────────
// List
// ─────────────────────────────────────────────────────────────────────────────

/**
 * GET /api/eraf/permissions — full list (no server paging; filter client-side).
 * Also invalidates the role-management permission catalog so the matrix
 * stays fresh after a create/update/delete.
 */
export function usePermissionList(filters?: PermissionListFilter) {
  return useQuery({
    queryKey: ['permission-management', 'list', filters ?? {}],
    queryFn: () => permissionApi.list(filters),
    staleTime: 30_000,
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Create
// ─────────────────────────────────────────────────────────────────────────────

export function useCreatePermission() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: CreatePermissionRequest) => permissionApi.create(body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['permission-management', 'list'] });
      // Keep role-management permission catalog in sync
      void queryClient.invalidateQueries({ queryKey: ['role-management', 'permissions'] });
    },
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Update
// ─────────────────────────────────────────────────────────────────────────────

export function useUpdatePermission(id: number) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: UpdatePermissionRequest) => permissionApi.update(id, body),
    onSuccess: (data) => {
      queryClient.setQueryData(['permission-management', 'detail', id], data);
      void queryClient.invalidateQueries({ queryKey: ['permission-management', 'list'] });
      void queryClient.invalidateQueries({ queryKey: ['role-management', 'permissions'] });
    },
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Delete
// ─────────────────────────────────────────────────────────────────────────────

export function useDeletePermission() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, force }: { id: number; force?: boolean }) =>
      permissionApi.delete(id, force),
    onSuccess: (_data, { id }) => {
      queryClient.removeQueries({ queryKey: ['permission-management', 'detail', id] });
      void queryClient.invalidateQueries({ queryKey: ['permission-management', 'list'] });
      void queryClient.invalidateQueries({ queryKey: ['role-management', 'permissions'] });
    },
  });
}
